import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    container: {
        marginTop: 10,
        marginBottom: 10,
    },
    heading: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 5,
    },
    table: {
        display: 'flex',
        width: '100%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#ECECEC',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#ECECEC',
        borderBottomStyle: 'solid',
        alignItems: 'center',
    },
    tableHeader: {
        backgroundColor: '#F0F4F8',
    },
    tableCell: {
        padding: 3,
        fontSize: 8,
        borderRightWidth: 1,
        borderRightColor: '#ECECEC',
        borderRightStyle: 'solid',
    },
    tableCellMode: {
        width: '20%',
        fontWeight: 'bold',
    },
    tableCellVariant: {
        flex: 1,
        textAlign: 'center',
    },
    statusCell: {
        padding: 3,
        fontSize: 8,
        borderRightWidth: 1,
        borderRightColor: '#ECECEC',
        borderRightStyle: 'solid',
        flex: 1,
        textAlign: 'center',
    },
    statusPass: {
        backgroundColor: '#4CAF50',
        color: 'white',
        padding: 2,
        fontSize: 8,
        textAlign: 'center',
        margin: 2,
    },
    statusFail: {
        backgroundColor: '#F44336',
        color: 'white',
        padding: 2,
        fontSize: 8,
        textAlign: 'center',
        margin: 2,
    },
    statusDataMissing: {
        backgroundColor: '#F79E19',
        color: 'white',
        padding: 2,
        fontSize: 8,
        textAlign: 'center',
        margin: 2,
    },
});

const failureModeLabels = {
    preShipLean: "Pre-Ship Lean",
    postShipLean: "Post-Ship Lean",
    preShipFTL: "Pre-Ship FTL",
    postShipFTL: "Post-Ship FTL",
    shipTestWalk: "Post Ship unit load position",
    stackTestLeanWeekly: "Stack Test Lean Weekly",
    stackTestCompression: "Stack UnitLoad Height",
};

interface FailureModeTablePDFProps {
    failureModes: Record<string, Record<string, string>>;
}

export const FailureModeTablePDF: React.FC<FailureModeTablePDFProps> = ({ failureModes, fullData, synopsisData }) => {


    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'pass':
                return styles.statusPass;
            case 'fail':
                return styles.statusFail;
            default:
                return styles.statusDataMissing;
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pass':
                return 'Pass';
            case 'fail':
                return 'Fail';
            case 'dataMissing':
                return 'Incomplete';
            default:
                return 'N/A';
        }
    };
    console.log(fullData, 'fullData');
    const determineFailureModeStatus = (additionalStats: any) => {
        if (!additionalStats) return 'dataMissing';

        // Check data fill status first
        if (additionalStats.dataFillStatus === 'dataMissing' || additionalStats.dataFillStatus === 'N/A' || additionalStats.dataFillStatus === 'Incomplete') {
            return 'dataMissing';
        }

        // If there's any data but the overall status is 'Fail', return 'fail'
        if (additionalStats.overallStatus === 'Fail') {
            return 'fail';
        }

        // Otherwise, if the data is complete or incomplete but passes, return 'pass'
        if (additionalStats.overallStatus === 'Pass') {
            return 'pass';
        }

        // If we can't determine, default to data missing
        return 'dataMissing';
    };

    // const realFailureData=()=>{
    const accurateFailureModes = JSON.parse(JSON.stringify(synopsisData.failureModes));

    // Process each variant (pallet type)
    Object.keys(accurateFailureModes).forEach(variant => {
        console.log(variant, 'variant')
        // Pre-Ship Lean
        if (fullData?.summaryData?.preShipLean?.additionalStats) {
            console.log(`Pre-Ship Lean data for ${variant}:`, fullData?.summaryData?.preShipLean)
            const variantStats = fullData?.summaryData?.preShipLean.additionalStats.find(
                (stats: any) => {
                    // Fix the comparison - extract the variant part from the stats.rowLabel
                    const parts = variant.split(" - ");
                    return parts.length > 1 && parts[1] === stats.rowLabel;
                }
            );
            console.log(`Pre-Ship Lean variant stats for ${variant}:`, variantStats)
            if (variantStats) {
                accurateFailureModes[variant].preShipLean = determineFailureModeStatus(variantStats);
            }
        }

        // Post-Ship Lean
        if (fullData?.summaryData?.postShipLean?.additionalStats) {
            const variantStats = fullData?.summaryData?.postShipLean.additionalStats.find(
                (stats: any) => {
                    const parts = variant.split(" - ");
                    return parts.length > 1 && parts[1] === stats.rowLabel;
                }
            );
            if (variantStats) {
                accurateFailureModes[variant].postShipLean = determineFailureModeStatus(variantStats);
            }
        }

        // Stack Test Lean Weekly
        if (fullData?.summaryData?.stackTestLean?.additionalStats) {
            const variantStats = fullData?.summaryData?.stackTestLean.additionalStats.find(
                (stats: any) => {
                    const parts = variant.split(" - ");
                    return parts.length > 1 && parts[1] === stats.rowLabel;
                }
            );
            if (variantStats) {
                accurateFailureModes[variant].stackTestLeanWeekly = determineFailureModeStatus(variantStats);
            }
        }

        // Pre-Ship FTL
        if (fullData?.summaryData?.preShipFTL?.additionalStats) {
            const variantStats = fullData?.summaryData?.preShipFTL.additionalStats.find(
                (stats: any) => {
                    const parts = variant.split(" - ");
                    return parts.length > 1 && parts[1] === stats.rowLabel;
                }
            );
            if (variantStats) {
                accurateFailureModes[variant].preShipFTL = determineFailureModeStatus(variantStats);
            }
        }

        // Post-Ship FTL
        if (fullData?.summaryData?.postShipFTL?.additionalStats) {
            const variantStats = fullData?.summaryData?.postShipFTL.additionalStats.find(
                (stats: any) => {
                    const parts = variant.split(" - ");
                    return parts.length > 1 && parts[1] === stats.rowLabel;
                }
            );
            if (variantStats) {
                accurateFailureModes[variant].postShipFTL = determineFailureModeStatus(variantStats);
            }
        }

        // Ship Test Walk (Unit Load Position)
        if (fullData?.summaryData?.unitLoadPosition?.additionalStats) {
            const variantStats = fullData?.summaryData?.unitLoadPosition.additionalStats.find(
                (stats: any) => {
                    const parts = variant.split(" - ");
                    return parts.length > 1 && parts[1] === stats.rowLabel;
                }
            );
            console.log(`Ship Walk Test for ${variant}:`, variantStats)
            if (variantStats) {
                accurateFailureModes[variant].shipTestWalk = determineFailureModeStatus(variantStats);
            }
        }

        // Stack Test Lean Weekly - Fix the bug where it was using shipTestWalkData instead of stackTestLeanWeekly
        if (fullData?.summaryData?.stackTestLean?.additionalStats) {
            const variantStats = fullData?.summaryData?.stackTestLean.additionalStats.find(
                (stats: any) => {
                    const parts = variant.split(" - ");
                    return parts.length > 1 && parts[1] === stats.rowLabel;
                }
            );
            console.log(`Stack Test Lean Weekly for ${variant}:`, variantStats)
            if (variantStats) {
                accurateFailureModes[variant].stackTestLeanWeekly = determineFailureModeStatus(variantStats);
            }
        }

        // Stack Test Compression (Unit Load Height)
        if (fullData?.summaryData?.stackTestCompression?.additionalStats) {
            const variantStats = fullData?.summaryData?.stackTestCompression.additionalStats.find(
                (stats: any) => {
                    const parts = variant.split(" - ");
                    return parts.length > 1 && parts[1] === stats.rowLabel;
                }
            );
            console.log(`Stack Test Compression for ${variant}:`, variantStats)
            if (variantStats) {
                accurateFailureModes[variant].stackTestCompression = determineFailureModeStatus(variantStats);
            }
        }
    });
    //     return accurateFailureModes;
    // }
    console.log('accurateFailureModes', accurateFailureModes)
    // If there are no failure modes or empty object, don't render anything
    if (!failureModes || Object.keys(failureModes).length === 0) {
        return null;
    }
    //this is accurateFailureModes
    // {
    //     "test - Control 1": {
    //         "preShipLean": "dataMissing",
    //         "postShipLean": "dataMissing",
    //         "preShipFTL": "pass",
    //         "postShipFTL": "fail",
    //         "shipTestWalk": "dataMissing",
    //         "stackTestLeanWeekly": "dataMissing",
    //         "stackTestCompression": "dataMissing",
    //         "damageToPrimaryPackage": "dataMissing",
    //         "damageToSecondaryPackage": "dataMissing",
    //         "damageToTertiaryPackage": "dataMissing"
    //     },
    //     "test-p1 - Variable 1": {
    //         "preShipLean": "dataMissing",
    //         "postShipLean": "dataMissing",
    //         "preShipFTL": "fail",
    //         "postShipFTL": "fail",
    //         "shipTestWalk": "dataMissing",
    //         "stackTestLeanWeekly": "pass",
    //         "stackTestCompression": "dataMissing",
    //         "damageToPrimaryPackage": "dataMissing",
    //         "damageToSecondaryPackage": "dataMissing",
    //         "damageToTertiaryPackage": "dataMissing"
    //     },
    //     "test-2 - Control 2": {
    //         "preShipLean": "dataMissing",
    //         "postShipLean": "dataMissing",
    //         "preShipFTL": "dataMissing",
    //         "postShipFTL": "dataMissing",
    //         "shipTestWalk": "dataMissing",
    //         "stackTestLeanWeekly": "fail",
    //         "stackTestCompression": "dataMissing",
    //         "damageToPrimaryPackage": "dataMissing",
    //         "damageToSecondaryPackage": "dataMissing",
    //         "damageToTertiaryPackage": "dataMissing"
    //     }
    // }
    return (
        <View style={styles.container}>
            <Text style={styles.heading}>Failure Modes</Text>
            <View style={styles.table}>
                {/* Header Row */}
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <View style={[styles.tableCell, styles.tableCellMode]}>
                        <Text>Failure Modes</Text>
                    </View>
                    {Object.keys(accurateFailureModes).map((variant) => (
                        <View key={variant} style={[styles.tableCell, styles.tableCellVariant]}>
                            <Text>{variant}</Text>
                        </View>
                    ))}
                </View>

                {/* Data Rows */}
                {Object.entries(failureModeLabels).map(([key, label]) => (
                    <View key={key} style={styles.tableRow}>
                        <View style={[styles.tableCell, styles.tableCellMode]}>
                            <Text>{label}</Text>
                        </View>
                        {Object.keys(accurateFailureModes).map((variant) => {
                            const status = accurateFailureModes[variant]?.[key] || 'dataMissing';
                            return (
                                <View key={`${key}-${variant}`} style={styles.statusCell}>
                                    <View style={getStatusStyle(status)}>
                                        <Text>{getStatusText(status)}</Text>
                                    </View>
                                </View>
                            );
                        })}
                    </View>
                ))}
            </View>
        </View>
    );
}; 